/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-09 14:45:24
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React from 'react';
import { Button, Icon, TextStyle } from '@shopify/polaris';
import {
  ArrowDownMinor,
  ArrowUpMinor,
  ExternalMinor,
  HideMinor,
  MobileCancelMajor,
  PinMajor
} from '@shopify/polaris-icons';
import { Field } from 'redux-form';
import { withI18n } from '@shopify/react-i18n';

import PinProducts from 'src/components/containers/merchandising-v2/rule/strategy/pin-products';
import Attributes from 'src/components/containers/merchandising-v2/rule/strategy/attributes';
import Banners from 'src/components/containers/merchandising-v2/rule/strategy/banners';

import { RenderToggleSwitch } from 'src/components/views/common/forms/render-toggle-switch';
import { FilterIconDisable } from 'src/components/containers/common/icon/filterIcon';
import { BannerIcon } from 'src/components/containers/common/icon/bannerIcon';
import { RankingIconDisable } from 'src/components/containers/common/icon/rankingIcon';
import { routes } from 'src/routes';
import magicBadgeWarning from 'src/assets/images/common/magic-badge-warning.png';
//
const SetupStrategy = props => {
  const {
    i18n,
    strategies,
    ruleType,
    history,
    selectedIndex,
    strategyTab,
    strategyProducts,
    activeStrategy,
    merchandisingRuleStrategyRef,
    onChangeTab,
    handleOpenStrategyCard,
    setSelectedAttribute,
    removeEmptyStrategies
  } = props;

  if (_.isEmpty(activeStrategy)) return <div></div>;

  const iconMapping = {
    pin: <Icon source={PinMajor} color="subdued" />,
    boost: <Icon source={ArrowUpMinor} color="subdued" />,
    demote: <Icon source={ArrowDownMinor} color="subdued" />,
    hide: <Icon source={HideMinor} color="subdued" />,
    filter: <Icon source={FilterIconDisable} color="subdued" />,
    banner: <Icon source={BannerIcon} color="subdued" />,
    productPerformanceRanking: <Icon source={RankingIconDisable} color="subdued" />
  };

  const goToProductPerformanceRanking = () => {
    history.push(routes.productPerformanceRanking.view());
  };
  const renderSettings = () => {
    switch (activeStrategy) {
      case 'pin': {
        return (
          <>
            <div className="card-section">
              <p className="default-text-style">
                {i18n.translate(`AdminHTML.MerchandisingV2.AttributesModal.pin_Description01`, {
                  t: (
                    <TextStyle variation="strong">
                      {i18n.translate(
                        `AdminHTML.MerchandisingV2.AttributesModal.pin_Description01_t`
                      )}
                    </TextStyle>
                  )
                })}
              </p>
              <p className="default-text-style">
                {i18n.translate(`AdminHTML.MerchandisingV2.AttributesModal.pin_Description02`, {
                  t: (
                    <TextStyle variation="strong">
                      {i18n.translate(
                        `AdminHTML.MerchandisingV2.AttributesModal.pin_Description02_t`
                      )}
                    </TextStyle>
                  )
                })}
              </p>
            </div>
            <div className="card-section">
              <PinProducts strategies={strategies} ruleType={ruleType} />
            </div>
          </>
        );
      }
      case 'productPerformanceRanking':
        return (
          <>
            <div className="card-section">
              <div className="default-text-style">
                <p>
                  {i18n.translate(
                    `AdminHTML.MerchandisingV2.AttributesModal.productPerformanceRanking_Description01`,
                    {
                      t: (
                        <span className="pfs-link" onClick={() => goToProductPerformanceRanking()}>
                          {i18n.translate(
                            `AdminHTML.MerchandisingV2.AttributesModal.productPerformanceRanking_Description01_t`
                          )}
                          <Icon source={ExternalMinor} />
                        </span>
                      )
                    }
                  )}
                </p>
                <p>
                  {i18n.translate(
                    `AdminHTML.MerchandisingV2.AttributesModal.productPerformanceRanking_Description02`
                  )}
                </p>
              </div>
            </div>
            <div className="card-section">
              <Field
                name="isEnablePerformanceRanking"
                component={RenderToggleSwitch}
                label={i18n.translate(
                  'AdminHTML.MerchandisingV2.AttributesModal.productPerformanceRanking_content'
                )}
              />
            </div>
          </>
        );
      case 'banner':
        return (
          <>
            <div className="card-section">
              <p>
                {i18n.translate(`AdminHTML.MerchandisingV2.AttributesModal.banner_Description`)}
              </p>
            </div>
            <div className="card-banner-section">
              <Banners
                type={activeStrategy}
                strategies={strategies}
                selectedIndex={selectedIndex}
              />
            </div>
          </>
        );
      // Case strategy is boost | demote | hide | filter
      default:
        return (
          <Attributes
            type={activeStrategy}
            strategies={strategies}
            strategyProducts={strategyProducts}
            ruleType={ruleType}
            selectedIndex={selectedIndex}
            strategyTab={strategyTab}
            onChangeTab={onChangeTab}
            parentRef={merchandisingRuleStrategyRef}
            removeEmptyStrategies={removeEmptyStrategies}
          />
        );
    }
  };

  return (
    <>
      {/* Set merchandising strategies: Pin-boost-demote-hide-filter */}
      <div className="merchandising-rule-strategy">
        <div ref={merchandisingRuleStrategyRef} style={{ maxHeight: '1000px', overflowY: 'auto' }}>
          <div className="card-section consequence-detail-header">
            <div
              style={{ marginBottom: 5 }}
              className="consequence-detail-title tile-set-condition">
              <div className={`title title-${activeStrategy}`}>
                <div>{iconMapping[activeStrategy]}</div>
                <div className="title-header">
                  <p>
                    {i18n.translate(
                      `AdminHTML.MerchandisingV2.Rule.card3_${_.upperFirst(
                        activeStrategy
                      )}_Products`
                    )}
                  </p>
                  {activeStrategy === 'productPerformanceRanking' && (
                    <img alt="magic-badge-warning" src={magicBadgeWarning} width={36} height={24} />
                  )}
                </div>
              </div>
            </div>
            <Button
              icon={MobileCancelMajor}
              color="base"
              plain
              onClick={() => {
                handleOpenStrategyCard('', true);
                setSelectedAttribute(-1);
              }}
            />
          </div>
          {renderSettings()}
        </div>
      </div>
    </>
  );
};
export default withI18n()(SetupStrategy);
